import * as THREE from 'three';

export const MAX_CASCADES = 4;

/** GLSL for cascaded shadow lookup (hardware PCF, rotated Poisson taps, cascade blending). */
export const shadowGLSL = /* glsl */ `
uniform sampler2DShadow uShadowMap;
uniform mat4 uShadowMat[${MAX_CASCADES}];
uniform vec4 uShadowCascade[${MAX_CASCADES}]; // xy atlas offset, z atlas scale, w world texel size
uniform vec4 uShadowParams;  // x atlas texel size, y cascade count, z enabled, w softness
uniform vec3 uShadowLightDir;

const vec2 POISSON12[12] = vec2[](
  vec2(-0.326, -0.406), vec2(-0.840, -0.074), vec2(-0.696, 0.457), vec2(-0.203, 0.621),
  vec2(0.962, -0.195), vec2(0.473, -0.480), vec2(0.519, 0.767), vec2(0.185, -0.893),
  vec2(0.507, 0.064), vec2(0.896, 0.412), vec2(-0.322, -0.933), vec2(-0.792, -0.598));

float shadowCascadeSample(int i, vec3 wp, vec3 N, float rot, out bool inside) {
  vec4 c = uShadowCascade[i];
  float NoL = clamp(dot(N, uShadowLightDir), 0.0, 1.0);
  // normal-offset + slope-scaled bias in world units
  vec3 p = wp + N * c.w * (1.2 + 1.5 * (1.0 - NoL)) + uShadowLightDir * c.w * 0.6;
  vec4 lp = uShadowMat[i] * vec4(p, 1.0);
  vec3 uvz = lp.xyz * 0.5 + 0.5;
  inside = all(greaterThan(uvz.xy, vec2(0.01))) && all(lessThan(uvz.xy, vec2(0.99))) && uvz.z < 1.0 && uvz.z > 0.0;
  if (!inside) return 1.0;
  vec2 auv = c.xy + uvz.xy * c.z;
  float r = uShadowParams.x * uShadowParams.w * (i == 0 ? 1.6 : 1.2);
  float s = 0.0;
  float cs = cos(rot), sn = sin(rot);
  mat2 R = mat2(cs, sn, -sn, cs);
  for (int k = 0; k < 12; k++) {
    s += texture(uShadowMap, vec3(auv + R * POISSON12[k] * r, uvz.z));
  }
  return s / 12.0;
}

float getShadow(vec3 wp, vec3 N, vec2 fragCoord) {
  if (uShadowParams.z < 0.5) return 1.0;
  float rot = hash12(fragCoord) * 6.2831853;
  int n = int(uShadowParams.y);
  for (int i = 0; i < ${MAX_CASCADES}; i++) {
    if (i >= n) break;
    bool inside;
    float s = shadowCascadeSample(i, wp, N, rot, inside);
    if (inside) {
      // blend toward the next cascade near the border of this one
      vec4 lp = uShadowMat[i] * vec4(wp, 1.0);
      vec2 e = abs(lp.xy);
      float edge = smoothstep(0.82, 0.97, max(e.x, e.y));
      if (edge > 0.0 && i + 1 < n) {
        bool in2;
        float s2 = shadowCascadeSample(i + 1, wp, N, rot, in2);
        if (in2) s = mix(s, s2, edge);
      }
      return s;
    }
  }
  return 1.0;
}
`;

export interface ShadowCaster {
  /** Render this caster's depth for the given cascade camera. */
  renderShadow(renderer: THREE.WebGLRenderer, cam: THREE.OrthographicCamera, cascade: number, nearDist: number): void;
}

/**
 * Stable cascaded shadow maps packed in a 2x2 atlas sharing one depth texture.
 */
export class Shadows {
  readonly target: THREE.WebGLRenderTarget;
  readonly cams: THREE.OrthographicCamera[] = [];
  readonly uniforms = {
    uShadowMap: { value: null as THREE.Texture | null },
    uShadowMat: { value: [] as THREE.Matrix4[] },
    uShadowCascade: { value: [] as THREE.Vector4[] },
    uShadowParams: { value: new THREE.Vector4(1 / 4096, 4, 1, 1.0) },
    uShadowLightDir: { value: new THREE.Vector3(0, 1, 0) },
  };
  cascadeCount = 4;
  splits = [24, 110, 520, 2600];
  readonly casters: ShadowCaster[] = [];
  readonly breakdown: Record<string, number> = {};
  private readonly size: number;

  constructor(cascadeSize: number) {
    this.size = cascadeSize;
    const atlas = cascadeSize * 2;
    const depth = new THREE.DepthTexture(atlas, atlas, THREE.FloatType);
    depth.format = THREE.DepthFormat;
    depth.compareFunction = THREE.LessEqualCompare;
    depth.minFilter = THREE.LinearFilter;
    depth.magFilter = THREE.LinearFilter;
    this.target = new THREE.WebGLRenderTarget(atlas, atlas, {
      depthBuffer: true,
      depthTexture: depth,
      type: THREE.UnsignedByteType,
      format: THREE.RGBAFormat,
      generateMipmaps: false,
    });
    this.uniforms.uShadowMap.value = depth;
    this.uniforms.uShadowParams.value.x = 1 / atlas;
    for (let i = 0; i < 4; i++) {
      this.cams.push(new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1));
      this.uniforms.uShadowMat.value.push(new THREE.Matrix4());
      this.uniforms.uShadowCascade.value.push(new THREE.Vector4((i % 2) * 0.5, Math.floor(i / 2) * 0.5, 0.5, 1));
    }
  }

  private readonly tmpV = new THREE.Vector3();
  private readonly corners = Array.from({ length: 8 }, () => new THREE.Vector3());

  update(renderer: THREE.WebGLRenderer, camera: THREE.PerspectiveCamera, lightDir: THREE.Vector3, enabled: boolean, heightAboveGround: number) {
    const u = this.uniforms;
    u.uShadowParams.value.y = this.cascadeCount;
    u.uShadowParams.value.z = enabled && lightDir.y > -0.02 ? 1 : 0;
    u.uShadowLightDir.value.copy(lightDir);
    if (u.uShadowParams.value.z < 0.5) return;

    // When flying high, push cascades outward.
    const k = THREE.MathUtils.clamp(heightAboveGround / 25, 1, 12);
    const near = camera.near;
    const fovY = THREE.MathUtils.degToRad(camera.fov);
    const tanY = Math.tan(fovY / 2);
    const tanX = tanY * camera.aspect;
    const camWorld = camera.matrixWorld;

    const up = Math.abs(lightDir.y) > 0.99 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
    const lightView = new THREE.Matrix4().lookAt(new THREE.Vector3(0, 0, 0), lightDir.clone().negate(), up);
    const lightViewInv = lightView.clone().invert(); // world -> light space (rotation only)

    renderer.setRenderTarget(this.target);
    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    this.target.scissorTest = false;
    this.target.viewport.set(0, 0, this.size * 2, this.size * 2);
    renderer.setRenderTarget(this.target);
    renderer.clear(true, true, true);

    let prevFar = near;
    for (let i = 0; i < this.cascadeCount; i++) {
      const far = this.splits[i] * k;
      const n0 = i === 0 ? near : prevFar * 0.92;
      prevFar = far;
      // frustum slice corners in world space
      let ci = 0;
      for (const d of [n0, far]) {
        for (const sx of [-1, 1]) for (const sy of [-1, 1]) {
          this.corners[ci++].set(sx * tanX * d, sy * tanY * d, -d).applyMatrix4(camWorld);
        }
      }
      // bounding sphere (rotation invariant -> stable)
      const center = new THREE.Vector3();
      for (const c of this.corners) center.add(c);
      center.multiplyScalar(1 / 8);
      let radius = 0;
      for (const c of this.corners) radius = Math.max(radius, c.distanceTo(center));
      radius = Math.ceil(radius * 16) / 16;
      const texelWorld = (2 * radius) / this.size;
      // snap center to texel grid in light space
      const lc = center.clone().applyMatrix4(lightViewInv);
      lc.x = Math.floor(lc.x / texelWorld) * texelWorld;
      lc.y = Math.floor(lc.y / texelWorld) * texelWorld;
      const snapped = lc.applyMatrix4(lightView);
      const depthRange = Math.max(radius * 2, 3000);
      const cam = this.cams[i];
      cam.left = -radius; cam.right = radius; cam.top = radius; cam.bottom = -radius;
      cam.near = 0.5; cam.far = depthRange + radius;
      cam.position.copy(snapped).addScaledVector(lightDir, depthRange);
      cam.up.copy(up);
      cam.lookAt(snapped);
      cam.updateMatrixWorld(true);
      cam.updateProjectionMatrix();
      u.uShadowMat.value[i].multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
      u.uShadowCascade.value[i].w = texelWorld;

      const vx = (i % 2) * this.size, vy = Math.floor(i / 2) * this.size;
      this.target.viewport.set(vx, vy, this.size, this.size);
      this.target.scissor.set(vx, vy, this.size, this.size);
      this.target.scissorTest = true;
      renderer.setRenderTarget(this.target);
      for (let ci = 0; ci < this.casters.length; ci++) {
        const t = renderer.info.render.triangles;
        this.casters[ci].renderShadow(renderer, cam, i, i === 0 ? 0 : n0 * 0.55);
        this.breakdown[`${i}:${ci}:${this.casters[ci].constructor.name}`] = renderer.info.render.triangles - t;
      }
    }
    this.target.scissorTest = false;
    this.target.viewport.set(0, 0, this.size * 2, this.size * 2);
    renderer.setRenderTarget(null);
    renderer.autoClear = prevAutoClear;
    void this.tmpV;
  }
}
